/**
 * Hybrid Product Swatch / Product Family component.
 *
 * Integrates with the theme's EXISTING variant system (variant-selects.js /
 * product-info.js / FoxTheme.pubsub) instead of re-implementing it:
 *   - When a swatch resolves to a color OPTION VALUE on the product currently
 *     being viewed, this script checks the matching native (hidden) color
 *     radio/select input and dispatches a real `change` event on it, so the
 *     theme's own `variant-selects` -> `product-info` pipeline drives price,
 *     media, availability, URL, SKU, add-to-cart, etc. exactly as if the
 *     shopper had clicked the native swatch themselves.
 *   - When a swatch resolves to another PRODUCT (a promoted color, or a color
 *     that lives on a sibling product), the swatch is a real <a href> and the
 *     browser navigates normally. On the destination page, this script reads
 *     the `swatch_color` / `swatch_opts` query params, re-creates the
 *     selection using the same native mechanism, then cleans the URL.
 *
 * Namespaced as `product-family-swatches` / `hybrid-*` to avoid colliding
 * with the theme's own `variant-selects` / `swatch-*` naming.
 */
(function () {
  if (customElements.get('product-family-swatches')) return;

  var PARAM_COLOR = 'swatch_color';
  var PARAM_OPTS = 'swatch_opts';
  var CHANGE_TIMEOUT = 2000;

  customElements.define(
    'product-family-swatches',
    class ProductFamilySwatches extends HTMLElement {
      connectedCallback() {
        this.colorOptionName = this.dataset.colorOptionName || '';

        this._onClick = this.handleClick.bind(this);
        this.addEventListener('click', this._onClick);

        // Keep the "active" swatch in sync whenever the native color option
        // changes for ANY reason (our own clicks, or anything else), even
        // though the native fieldset gets fully re-rendered/replaced by
        // product-info.js on every variant change. Delegating on `document`
        // means we never hold a stale reference to it.
        this._onNativeChange = this.handleNativeChange.bind(this);
        document.addEventListener('change', this._onNativeChange);

        this.processUrlParams();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this._onClick);
        document.removeEventListener('change', this._onNativeChange);
      }

      get productInfoRoot() {
        return this.closest('product-info') || document;
      }

      get nativeColorFieldset() {
        // Marked by product-variant-picker.liquid when a swatch_family is
        // active on the option matching this component's color option name.
        return this.productInfoRoot.querySelector('.hybrid-swatches-native-hidden');
      }

      findOptionInput(fieldset, value) {
        if (!fieldset || !value) return null;
        var valueDowncase = value.toLowerCase();
        var inputs = fieldset.querySelectorAll('input[type="radio"], select option');
        for (var i = 0; i < inputs.length; i++) {
          var input = inputs[i];
          var inputValue = (input.value || input.textContent || '').trim().toLowerCase();
          if (inputValue === valueDowncase) return input;
        }
        return null;
      }

      /**
       * Selects `value` on `fieldset` using the theme's native mechanism
       * (check the input / select the option, then dispatch `change`), and
       * resolves once the theme has finished processing that change (or after
       * a timeout, so a misconfigured/unavailable value can never hang).
       */
      selectOptionValue(fieldset, value) {
        return new Promise((resolve) => {
          var input = this.findOptionInput(fieldset, value);
          if (!input) {
            resolve(false);
            return;
          }

          var timer = setTimeout(() => {
            document.removeEventListener('variant:changed', onChanged);
            resolve(true);
          }, CHANGE_TIMEOUT);

          function onChanged() {
            clearTimeout(timer);
            document.removeEventListener('variant:changed', onChanged);
            resolve(true);
          }
          document.addEventListener('variant:changed', onChanged);

          var dispatchTarget = input;
          if (input.tagName === 'OPTION') {
            dispatchTarget = input.closest('select');
            dispatchTarget.value = input.value;
          } else {
            input.checked = true;
          }
          dispatchTarget.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      /**
       * A variant-backed swatch can only work if the family's
       * `color_option_name` matches the product's real option name and the
       * member's `target_variant_value` matches one of that option's values.
       * Surface the exact reason in the console instead of failing silently.
       */
      warnMissingOption(fieldset, value) {
        if (!fieldset) {
          console.warn(
            '[product-family-swatches] Native color option not found on this product. ' +
              'The family\'s "color_option_name" ("' + this.colorOptionName + '") must match the product option name exactly.'
          );
          return;
        }
        var available = Array.prototype.map.call(
          fieldset.querySelectorAll('input[type="radio"], select option'),
          (el) => el.value
        );
        console.warn(
          '[product-family-swatches] Value "' + value + '" not found in the product\'s color option. ' +
            'Check the member\'s "target_variant_value". Available values: ' + JSON.stringify(available)
        );
      }

      /**
       * Click handling for the swatches themselves.
       */
      handleClick(event) {
        var link = event.target.closest('[data-hybrid-swatch]');
        if (!link || !this.contains(link)) return;

        var mode = link.dataset.swatchMode;
        var isCurrentProduct = link.dataset.isCurrentProduct === 'true';
        var targetVariantValue = link.dataset.targetVariantValue;

        if (mode === 'product' && isCurrentProduct) {
          // Already viewing this exact standalone product - this swatch is
          // purely a "current selection" indicator, not a navigation target.
          event.preventDefault();
          return;
        }

        if (mode === 'variant' && isCurrentProduct) {
          // The color lives on the product we're already on: never navigate,
          // just drive the existing native color option.
          event.preventDefault();
          var fieldset = this.nativeColorFieldset;
          this.selectOptionValue(fieldset, targetVariantValue).then((found) => {
            if (!found) this.warnMissingOption(fieldset, targetVariantValue);
          });
          return;
        }

        // Cross-product navigation (variant-on-sibling-product, or a fully
        // standalone product). Let the real <a href> navigate - this keeps
        // the link crawlable and functional without JS. We only enrich the
        // href just-in-time with the shopper's other current selections
        // (size/pleat/width/etc.) so the destination page can try to
        // preserve them.
        var preserved = this.collectOtherOptionValues();
        if (Object.keys(preserved).length) {
          try {
            var url = new URL(link.href, window.location.origin);
            url.searchParams.set(PARAM_OPTS, JSON.stringify(preserved));
            link.href = url.toString();
          } catch (e) {
            // If URL construction fails for any reason, fall back to the
            // plain href already on the anchor - navigation still works.
          }
        }
        // No preventDefault: the anchor navigates natively.
      }

      /**
       * Reads every OTHER (non-color) option currently selected on this page,
       * keyed by option name, so they can be forwarded to a sibling/standalone
       * product as a best-effort preservation hint.
       */
      collectOtherOptionValues() {
        var result = {};
        var root = this.productInfoRoot;
        var labels = root.querySelectorAll('[data-selected-swatch-value]');
        labels.forEach((label) => {
          var optionName = label.getAttribute('data-selected-swatch-value');
          if (!optionName || optionName.toLowerCase() === this.colorOptionName.toLowerCase()) return;
          var fieldset = label.closest('.product-form__input');
          if (!fieldset) return;
          var checked = fieldset.querySelector('input:checked, select');
          var value = checked ? checked.value : null;
          if (value) result[optionName] = value;
        });
        return result;
      }

      /**
       * Keeps the visually "active" swatch synced with the native color
       * option, however it was changed.
       */
      handleNativeChange(event) {
        var fieldset = this.nativeColorFieldset;
        if (!fieldset || !fieldset.contains(event.target)) return;

        var value = event.target.value;
        if (event.target.tagName === 'SELECT' && event.target.selectedOptions.length) {
          value = event.target.selectedOptions[0].value;
        }
        if (!value) return;

        var valueDowncase = value.toLowerCase();
        var selectedLabel = value;
        var swatches = this.querySelectorAll('[data-hybrid-swatch][data-swatch-mode="variant"][data-is-current-product="true"]');
        swatches.forEach((swatch) => {
          var isMatch = (swatch.dataset.targetVariantValue || '').toLowerCase() === valueDowncase;
          swatch.classList.toggle('is-selected', isMatch);
          if (isMatch) {
            swatch.setAttribute('aria-current', 'true');
            selectedLabel = swatch.dataset.label || value;
          } else {
            swatch.removeAttribute('aria-current');
          }
        });

        // The "Renk: <selected>" text is server-rendered once; keep it in sync.
        var labelEl = this.querySelector('[data-hybrid-selected-label]');
        if (labelEl) labelEl.textContent = selectedLabel;
      }

      /**
       * On load: detect `swatch_color` / `swatch_opts` query params left by a
       * cross-product swatch click, reproduce that selection using the native
       * mechanism, then strip the params from the URL so a refresh/back-nav
       * doesn't re-trigger anything (guards against loops).
       */
      async processUrlParams() {
        if (window.__hybridSwatchParamsHandled) return;

        var params = new URLSearchParams(window.location.search);
        var swatchColor = params.get(PARAM_COLOR);
        var swatchOptsRaw = params.get(PARAM_OPTS);
        if (!swatchColor && !swatchOptsRaw) return;

        window.__hybridSwatchParamsHandled = true;

        if (swatchColor) {
          await this.selectOptionValue(this.nativeColorFieldset, swatchColor);
        }

        if (swatchOptsRaw) {
          var opts = {};
          try {
            opts = JSON.parse(swatchOptsRaw);
          } catch (e) {
            opts = {};
          }

          for (var optionName in opts) {
            if (!Object.prototype.hasOwnProperty.call(opts, optionName)) continue;
            var fieldset = this.findFieldsetByOptionName(optionName);
            // Never force an invalid/unavailable combination: only select it
            // if that value actually exists (as an enabled input) right now.
            if (fieldset) {
              await this.selectOptionValue(fieldset, opts[optionName]);
            }
          }
        }

        this.cleanUrlParams();
      }

      findFieldsetByOptionName(name) {
        var nameDowncase = name.toLowerCase();
        var labels = this.productInfoRoot.querySelectorAll('[data-selected-swatch-value]');
        for (var i = 0; i < labels.length; i++) {
          var label = labels[i];
          if ((label.getAttribute('data-selected-swatch-value') || '').toLowerCase() === nameDowncase) {
            return label.closest('.product-form__input');
          }
        }
        return null;
      }

      cleanUrlParams() {
        var url = new URL(window.location.href);
        url.searchParams.delete(PARAM_COLOR);
        url.searchParams.delete(PARAM_OPTS);
        window.history.replaceState({}, '', url.toString());
      }
    }
  );
})();
